import React, { memo, useMemo } from 'react';
import { useTextParsing, useMentionInteractions } from '../../hooks';
import { MentionTooltip } from '../CommonTooltip/MentionTooltip/MentionTooltip';
import type { TextWithMentionsProps } from './TextWithMentions.types';

const TextWithMentionsComponent: React.FC<TextWithMentionsProps> = ({
  text,
  commentMentions,
  tooltipStyle,
  displayMode
}) => {
  const { parseText } = useTextParsing();
  // Use unified tooltip system with rich mention data
  const {
    tooltipState,
    handleRichMentionHover,
    handleMentionLeave,
    handleHyperlinkHover,
    handleHyperlinkLeave
  } = useMentionInteractions();

  // Parse text using the unified hook - much simpler!
  const textParts = useMemo(() => {
    try {
      const parsedText = parseText(text, commentMentions);
      return parsedText;
    } catch (error) {
      console.warn('Error parsing text:', error);
      return [{ text, type: 'text' as const }];
    }
  }, [text, commentMentions, parseText]);

  return (
    <>
      <span style={{ lineHeight: '18.2px' }}>
        {textParts.map((part, index) => {
          if (
            part.type === 'mention' &&
            part.username &&
            part.email &&
            part.name
          ) {
            return (
              <span
                key={index}
                className="comment-text__mention"
                onMouseEnter={e =>
                  handleRichMentionHover(
                    {
                      username: part.username!,
                      email: part.email!,
                      name: part.name!
                    },
                    e
                  )
                }
                onMouseLeave={handleMentionLeave}
              >
                {part.text}
              </span>
            );
          }
          if (part.type === 'hyperlink' && part.url) {
            return (
              <a
                key={index}
                href={part.url}
                className="comment-text__hyperlink"
                target="_blank"
                rel="noopener noreferrer"
                title={part.url}
                onMouseEnter={e =>
                  handleHyperlinkHover(part.url!, part.text, e)
                }
                onMouseLeave={handleHyperlinkLeave}
              >
                {part.url}
              </a>
            );
          }
          return <span key={index}>{part.text}</span>;
        })}
      </span>

      <MentionTooltip
        show={tooltipState.show}
        position={tooltipState.position}
        user={tooltipState.user}
        hyperlink={tooltipState.hyperlink}
        loading={tooltipState.loading}
        type={tooltipState.type}
        style={tooltipStyle}
        displayMode={displayMode}
      />
    </>
  );
};

export const TextWithMentions = memo(TextWithMentionsComponent);
